



















































import {
  computed,
  defineNuxtComponent,
  onMounted,
  ref
} from '~/utils/nuxt3-migration'
import { CategoryId } from '~/models/category/types'
import { useDep } from '~/compositions/dependency-container'
import ClassifiedSearchService from '~/services/search/ClassifiedSearchService'
import { formatNumber } from '~/utils/number'
import { useRequestError } from '~/compositions/request-error'
import { useI18n } from '~/compositions/i18n'
import { defineComponentTranslations } from '~/utils/i18n'
import { Searchable } from '~/models/search/types'
import CPostcodeAutocomplete from '~/components/shared/configurable/form/autocomplete/CPostcodeAutocomplete.vue'

export default defineNuxtComponent({
  components: { CPostcodeAutocomplete },
  setup() {
    const classifiedSearchService = useDep(ClassifiedSearchService)
    const handleRequestError = useRequestError()
    const { t } = useI18n()

    const loading = ref(false)
    const facets = ref([])
    const count = ref(0)
    const resultsUrl = ref('')
    const selectedOfferType = ref(null)
    const selectedCategory = ref(null)
    const selectedPostcode = ref({
      name: ''
    })

    onMounted(fetchFacets)

    const offerTypeFacet = computed(() =>
      facets.value.find(
        (facet: any) => facet.type === 'CategoryTreeSearchHandler'
      )
    )

    const categoryFacet = computed(() =>
      facets.value.find(
        (facet: any) => facet.type === 'CategoryTreeSearchHandler'
      )
    )

    const postcodeFacet = computed(() =>
      facets.value.find(
        (facet: any) => facet.type === 'PostcodeDistanceSearchHandler'
      )
    )

    const offerTypeOptions = computed(() => {
      const labels = {
        [CategoryId.PLOT_SALE]: t('buy_real_estate'),
        [CategoryId.PLOT_RENT]: t('rent_real_estate'),
        [CategoryId.PLOT_BOOKING]: t('book_real_estate'),
        [CategoryId.PLOT_WANTED_PROPERTY]: t('view_wanted_real_estate')
      }

      return offerTypeFacet.value.searchables.map((s: Searchable) => ({
        value: s.v,
        label: `${labels[s.v] || s.n} (${formatNumber(s.c)})`
      }))
    })

    const categoryOptions = computed(() => {
      const selectedCategoryChildren = categoryFacet.value.searchables.find(
        (item: any) => item.k?.length > 0
      )?.k

      return (selectedCategoryChildren || []).map((s: Searchable) => ({
        value: s.v,
        label: `${s.n} (${formatNumber(s.c)})`
      }))
    })

    async function fetchFacets() {
      loading.value = true
      try {
        const {
          targetSeoUrl,
          facets: fetchedFacets,
          stats
        } = await classifiedSearchService.getFacets({
          category: selectedCategory.value
            ? [CategoryId.PLOT, selectedCategory.value]
            : [CategoryId.PLOT],
          offertype: selectedOfferType.value,
          postcode: selectedPostcode.value.name || ''
        })
        resultsUrl.value = targetSeoUrl
        animateCounter(count.value, stats.total)
        facets.value = fetchedFacets
      } catch (error) {
        handleRequestError(error)
      } finally {
        loading.value = false
      }
    }

    function animateCounter(start: number, end: number) {
      // Skip animation on server-side
      if (typeof window === 'undefined' || typeof performance === 'undefined') {
        count.value = end
        return
      }

      const duration = 1000
      const startTime = performance.now()

      const animate = (currentTime: number) => {
        const elapsed = currentTime - startTime
        const progress = Math.min(elapsed / duration, 1)
        count.value = Math.floor(start + (end - start) * progress)

        if (progress < 1) {
          requestAnimationFrame(animate)
        } else {
          count.value = end
        }
      }

      requestAnimationFrame(animate)
    }

    function onOfferTypeChange() {
      selectedCategory.value = null
      fetchFacets()
    }

    function onCategoryChange() {
      fetchFacets()
    }

    function onPostcodeChange() {
      fetchFacets()
    }

    return {
      facets,
      loading,
      selectedOfferType,
      selectedCategory,
      selectedPostcode,
      offerTypeOptions,
      categoryOptions,
      count,
      resultsUrl,
      offerTypeFacet,
      categoryFacet,
      postcodeFacet,
      onOfferTypeChange,
      onCategoryChange,
      onPostcodeChange,
      formatNumber
    }
  },
  i18n: defineComponentTranslations({
    buy_real_estate: {
      en: 'Buy',
      el: 'Αγοράσω'
    },
    rent_real_estate: {
      en: 'Rent',
      el: 'Νοικιάσω'
    },
    book_real_estate: {
      en: 'Book',
      el: 'Κάνω κράτηση'
    },
    view_wanted_real_estate: {
      en: 'View wanted classifieds',
      el: 'Δω αγγελίες ζήτησης'
    }
  })
})
